'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Offer } from '@/services/dataTypes'
import {
    calculateOfferDuration,
    discardHTMLTags,
    getBaseImageUrl,
    getCurrencySymbol,
    getFinalDiscountTag,
    getMerchantHref,
    getProductDetailHref,
} from '@/constants/hooks'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import { toast } from 'react-toastify'
import {
    Star,
    ShoppingBag,
    ShoppingCart,
    Copy,
    Check,
    ShieldCheck,
    Tag,
    Clock,
    ChevronDown,
    ChevronRight,
    FileText,
    CheckCircle2,
    Layers,
    Store,
    Sparkles,
    Zap,
    Shield,
    MessageSquare,
} from 'lucide-react'
import CommentSection from './CommentSection'
import EventOfferCard from './EventOfferCard'
import PageHeader from './PageHeader'

const C = {
    PRIMARY: '#F8F246',
    SECONDARY: '#2CBBC3',
    BROWN: '#8C5826',
    BORDER: '#f0ede8',
    TEXT: '#1a1a1a',
    MUTED: '#6b7280',
    BG: '#fffaf5',
}

interface Props {
    product: Offer
    merchantRes: any
    categories: any[]
    relatedProducts: any[]
    companyDomain: string
    companyId: string
    storeSlug: string
    slugType: string
    currentMerchantSlug: string
    categorySlug?: string
}

const ProductDetailInteractive = ({
    product,
    merchantRes,
    categories,
    relatedProducts,
    companyDomain,
    companyId,
    storeSlug,
    slugType,
    currentMerchantSlug,
    categorySlug,
}: Props) => {
    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const hasDiscount = originalPrice > 0 && salePrice > 0 && originalPrice > salePrice
    const discountPercent = hasDiscount ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(product?.offer_title, discountPercent)
    const savings = hasDiscount ? (originalPrice - salePrice).toFixed(2) : null
    const currencySymbol = getCurrencySymbol(product?.currency)

    const duration = calculateOfferDuration(product?.end_date)
    const isLifetime = !product?.end_date || duration === 'Life Time'

    const merchantName = product?.merchant?.merchant_name || merchantRes?.merchant_name || currentMerchantSlug
    const merchantLogo = product?.merchant?.merchant_logo || merchantRes?.merchant_logo
    const merchantHref = getMerchantHref(product?.merchant || merchantRes, storeSlug, slugType)
    const formattedCategory = product?.category?.name || 'Products'

    // Combine FAQs from product or merchant
    const allFaqs = Array.isArray(product?.faqs) && product.faqs.length > 0
        ? product.faqs
        : (Array.isArray(merchantRes?.faqs) ? merchantRes.faqs : [])

    const [openFaq, setOpenFaq] = useState<number | null>(null)
    const mainProductImage = getBaseImageUrl(companyDomain, product?.product_image || merchantLogo, '')

    // Interactive copy state
    const [copied, setCopied] = useState<boolean>(false)

    const handleCopyCode = async () => {
        if (!product?.coupon_code) return
        try {
            await navigator.clipboard.writeText(product.coupon_code.trim())
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            try {
                const el = document.createElement('textarea')
                el.value = product.coupon_code.trim()
                el.style.position = 'fixed'
                el.style.opacity = '0'
                document.body.appendChild(el)
                el.focus()
                el.select()
                document.execCommand('copy')
                document.body.removeChild(el)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            } catch {
                toast.error('Please copy the code manually.')
            }
        }
    }

    const cleanDetail = product?.offer_detail ? discardHTMLTags(product.offer_detail) : ''
    const ratingVal = 4.8

    return (
        <div className="min-h-screen pb-16 font-sans" style={{ background: C.BG }}>

            {/* ━━━ Page Header with Breadcrumb ━━━ */}
            <PageHeader
                title={merchantName}
                highlight="Deal Details"
                subtitle={discardHTMLTags(product?.offer_title) || ''}
                badge="Verified Deal"
                breadcrumbs={[
                    { label: 'Products', href: '/products' },
                    { label: merchantName, href: merchantHref },
                    ...(product?.category?.slug
                        ? [{ label: formattedCategory, href: `/products/${currentMerchantSlug}/${product.category.slug}` }]
                        : []),
                    { label: discardHTMLTags(product?.offer_title) || 'Deal' },
                ]}
                stats={[
                    { label: 'Store', value: merchantName, icon: Store },
                    { label: 'Category', value: formattedCategory, icon: Tag },
                    { label: 'Status', value: '100% Verified', icon: ShieldCheck },
                ]}
            />

            {/* ━━━ MAIN CONTAINER ━━━ */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-6 space-y-4">

                {/* ━━━ HERO SHOWCASE GRID (Theme 12 Compact Sizing) ━━━ */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start pt-1">

                    {/* LEFT COLUMN: Product Image Showcase Card (lg:col-span-5) */}
                    <div className="lg:col-span-5">
                        <div className="relative bg-white rounded-2xl border border-[#f0ede8] p-4 sm:p-6 shadow-xs overflow-hidden flex items-center justify-center min-h-[250px] sm:min-h-[280px] group">
                            {/* Floating Discount Badge */}
                            {(finalDiscountTag || discountPercent) && (
                                <div className="absolute top-3 left-3 z-10">
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider shadow-2xs"
                                        style={{ background: '#FF9934', color: '#ffffff' }}>
                                        <Tag size={10} /> {finalDiscountTag || `${discountPercent}% OFF`}
                                    </span>
                                </div>
                            )}

                            {/* Floating Duration / Expiry Badge */}
                            {duration && !isLifetime && (
                                <div className="absolute top-3 right-3 z-10">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-[#fffbf0] text-[#8C5826] border border-[#F8F246] shadow-2xs">
                                        <Clock size={11} className="text-[#8C5826]" /> {duration}
                                    </span>
                                </div>
                            )}

                            {/* Product Image Stage */}
                            <div className="relative w-full h-[180px] sm:h-[220px] flex items-center justify-center">
                                <Image
                                    src={mainProductImage}
                                    alt={discardHTMLTags(product.offer_title) || 'Product Image'}
                                    fill
                                    className="object-contain transition-transform duration-300 group-hover:scale-105 drop-shadow-xs"
                                    priority
                                    unoptimized
                                />
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Product Title, Pricing & CTAs (lg:col-span-7) */}
                    <div className="lg:col-span-7 space-y-3.5">

                        {/* Top Badges & Product Title */}
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                                    <ShieldCheck size={11} className="text-emerald-600" /> Verified Deal
                                </span>
                                {product?.offer_type?.name && (
                                    <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md border border-gray-200/60 capitalize">
                                        {product.offer_type.name}
                                    </span>
                                )}
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#fffbf0] text-[#8C5826] border border-[#F8F246]">
                                    {merchantName}
                                </span>
                            </div>

                            <h1 className="text-lg sm:text-xl md:text-2xl font-black text-gray-900 leading-snug tracking-tight max-w-xl">
                                {discardHTMLTags(product?.offer_title)}
                            </h1>
                        </div>

                        {/* Rating Row */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-0.5">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                        key={s}
                                        size={14}
                                        className="text-amber-400 fill-amber-400"
                                    />
                                ))}
                            </div>
                            <span className="text-xs font-bold text-gray-700">{ratingVal}</span>
                            <span className="text-xs text-gray-400">(140+ verified ratings)</span>
                        </div>

                        {/* ── Price Block (Theme 12 Proportions) ── */}
                        {(salePrice > 0 || originalPrice > 0) && (
                            <div className="space-y-2 pt-0.5">
                                <div className="flex items-baseline gap-3 flex-wrap">
                                    {salePrice > 0 ? (
                                        <span className="text-2xl sm:text-3xl font-black text-gray-900 leading-none tracking-tight">
                                            {currencySymbol}{salePrice.toFixed(2)}
                                        </span>
                                    ) : (
                                        <span className="text-2xl sm:text-3xl font-black text-gray-900 leading-none tracking-tight">
                                            {currencySymbol}{originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                    {originalPrice > 0 && salePrice > 0 && originalPrice > salePrice && (
                                        <span className="text-sm text-gray-400 line-through font-medium">
                                            {currencySymbol}{originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                    {discountPercent && discountPercent > 0 && (
                                        <span className="inline-flex items-center text-[10px] font-black text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                                            {discountPercent}% OFF
                                        </span>
                                    )}
                                </div>

                                {/* Savings callout */}
                                {savings && (
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-lg w-fit">
                                        <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                                        <span className="text-xs font-semibold text-emerald-800">
                                            You save <span className="font-black">{currencySymbol}{savings}</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action Buttons: Buy Now & Visit Store (Compact Auto-Width) */}
                        <div className="flex items-center flex-wrap gap-2.5 pt-0.5">
                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-fit inline-flex items-center justify-center gap-1.5 px-5 h-10 bg-[#F8F246] hover:bg-[#f3ed35] text-[#8C5826] border border-[#8C5826]/20 font-bold text-xs uppercase tracking-wider rounded-xl transition-all duration-200 shadow-sm active:scale-95 cursor-pointer shrink-0"
                            >
                                <ShoppingBag size={14} />
                                <span>{product?.coupon_code ? 'Get Code & Buy Now' : 'Buy Now'}</span>
                            </OfferOutUrl>

                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-fit inline-flex items-center justify-center gap-1.5 px-4 h-10 bg-white hover:bg-[#fffbf0] text-gray-700 border border-[#f0ede8] font-bold text-xs uppercase tracking-wider rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
                            >
                                <ShoppingCart size={14} />
                                <span>Visit Store</span>
                            </OfferOutUrl>
                        </div>

                        {/* Coupon Code Section (if present) */}
                        {product?.coupon_code && (
                            <div className="bg-[#fffbf0] border border-dashed border-[#F8F246] p-3 sm:p-3.5 rounded-xl flex items-center justify-between gap-3 shadow-2xs max-w-md">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-wider text-[#8C5826] flex items-center gap-1">
                                        <Tag size={11} /> Promo Code
                                    </span>
                                    <p className="text-base sm:text-lg font-mono font-black text-[#8C5826] tracking-wider mt-0.5 select-all">
                                        {product.coupon_code}
                                    </p>
                                </div>
                                <button
                                    onClick={handleCopyCode}
                                    className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
                                    style={{
                                        background: copied ? '#16a34a' : C.BROWN,
                                        color: '#ffffff',
                                    }}
                                >
                                    {copied ? <Check size={13} /> : <Copy size={13} />}
                                    <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                                </button>
                            </div>
                        )}

                        {/* Trust & Guarantee Indicators */}
                        <div className="flex items-center gap-3 flex-wrap text-[10px] font-semibold text-gray-400 pt-1">
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                                <ShieldCheck size={12} className="text-emerald-600" /> 100% Verified
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1 text-gray-600 font-medium">
                                <Zap size={11} className="text-amber-500" /> Direct Link
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1 text-gray-600 font-medium">
                                <Clock size={11} className="text-gray-400" /> Daily Updated
                            </span>
                        </div>
                    </div>
                </div>

                {/* ━━━ LOWER SECTION: Product Description, FAQs & Sidebar ━━━ */}
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                    {/* ── LEFT COLUMN (lg:col-span-8): Description & FAQs ── */}
                    <div className="lg:col-span-8 space-y-6">

                        {/* Full Product Description */}
                        {cleanDetail && (
                            <div className="bg-white border border-[#f0ede8] rounded-2xl p-5 sm:p-6 shadow-xs space-y-3">
                                <div className="flex items-center gap-2 pb-2.5 border-b border-[#f0ede8]">
                                    <FileText size={16} style={{ color: C.BROWN }} />
                                    <h2 className="text-xs font-bold uppercase tracking-wider text-gray-900">
                                        Product Description & Overview
                                    </h2>
                                </div>

                                <div className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                    {product.offer_detail ? (
                                        <div
                                            className="prose prose-amber prose-xs sm:prose-sm max-w-none text-gray-600 leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: product.offer_detail }}
                                        />
                                    ) : (
                                        <p>{cleanDetail}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* FAQs Section */}
                        {allFaqs.length > 0 && (
                            <div className="bg-white rounded-2xl border border-[#f0ede8] p-5 sm:p-6 shadow-xs space-y-3">
                                <div className="flex items-center justify-between pb-2.5 border-b border-[#f0ede8]">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-900 flex items-center gap-1.5">
                                        Frequently Asked Questions
                                    </h3>
                                    <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                        {allFaqs.length} FAQs
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {allFaqs.map((faq: any, idx: number) => (
                                        <details
                                            key={faq.id || idx}
                                            className="group border border-[#f0ede8] rounded-xl p-3 transition-all duration-200 open:bg-[#fffbf0]/40"
                                        >
                                            <summary className="text-xs font-bold text-gray-800 cursor-pointer list-none flex items-center justify-between gap-2">
                                                <span>{faq.question || faq.title}</span>
                                                <ChevronDown size={14} className="text-gray-400 group-open:rotate-180 transition-transform duration-200 shrink-0" />
                                            </summary>
                                            <div
                                                className="text-xs text-gray-600 mt-2 pt-2 border-t border-[#f0ede8] leading-relaxed prose prose-amber prose-xs max-w-none"
                                                dangerouslySetInnerHTML={{ __html: faq.answer || faq.description || '' }}
                                            />
                                        </details>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Customer Reviews & Feedback Section */}
                        <div className="bg-white rounded-2xl border border-[#f0ede8] p-5 sm:p-6 shadow-xs space-y-3">
                            <div className="flex items-center gap-2 pb-2.5 border-b border-[#f0ede8]">
                                <MessageSquare size={16} style={{ color: C.BROWN }} />
                                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-900">
                                    Customer Reviews & Discussion
                                </h3>
                            </div>
                            <CommentSection offer_id={product?.unique_id} company_id={companyId} />
                        </div>
                    </div>

                    {/* ── RIGHT COLUMN (lg:col-span-4): Sidebar (Store Profile, Categories, Guarantee) ── */}
                    <div className="lg:col-span-4 space-y-5 lg:sticky lg:top-6">

                        {/* 1. Official Store Partner Card */}
                        <div className="bg-white rounded-2xl border border-[#f0ede8] p-4 shadow-xs space-y-3">
                            <div className="flex items-center gap-3">
                                {merchantLogo && (
                                    <div className="w-11 h-11 rounded-xl bg-[#fffbf0] border border-[#f0ede8] p-1 flex items-center justify-center shrink-0 shadow-2xs">
                                        <Image
                                            src={getBaseImageUrl(companyDomain, merchantLogo, '')}
                                            alt={merchantName}
                                            width={36}
                                            height={36}
                                            className="object-contain"
                                            unoptimized
                                        />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <p className="text-xs font-bold text-gray-900 truncate">{merchantName}</p>
                                        <ShieldCheck size={12} className="text-emerald-600 shrink-0" />
                                    </div>
                                    <p className="text-[10px] text-gray-400">Official Store Partner</p>
                                </div>
                            </div>

                            <div className="h-px bg-[#f0ede8]" />

                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-full flex items-center justify-center gap-1.5 bg-[#8C5826] hover:bg-[#73461d] text-white font-bold text-[11px] uppercase tracking-wider py-2.5 rounded-xl transition-all shadow-xs active:scale-[0.98] cursor-pointer"
                            >
                                <Store size={13} />
                                <span>Visit Official Store</span>
                            </OfferOutUrl>

                            <Link
                                href={merchantHref}
                                className="block text-center text-[11px] font-semibold text-gray-500 hover:text-[#8C5826] hover:underline transition-colors"
                            >
                                View all {merchantName} coupons & deals →
                            </Link>
                        </div>

                        {/* 2. Shop by Category Card */}
                        {Array.isArray(categories) && categories.length > 0 && (
                            <div className="bg-white rounded-2xl border border-[#f0ede8] shadow-xs overflow-hidden">
                                <div className="px-4 py-3 border-b border-[#f0ede8] bg-[#fffbf5] flex items-center gap-2">
                                    <Layers size={13} style={{ color: C.BROWN }} />
                                    <h3 className="text-xs font-bold text-[#8C5826] uppercase tracking-wider">
                                        {merchantName} Categories
                                    </h3>
                                </div>
                                <div className="p-2 space-y-1">
                                    {categories.slice(0, 8).map((cat: any, i: number) => (
                                        <Link
                                            key={cat.slug || i}
                                            href={`/products/${currentMerchantSlug}/${cat.slug}`}
                                            className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-[#fffaf5] group transition-colors text-xs font-medium text-gray-700 hover:text-[#8C5826]"
                                        >
                                            <span className="truncate">{cat.name}</span>
                                            <ChevronRight size={12} className="text-gray-300 group-hover:text-[#8C5826] transition-colors shrink-0" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 3. Verified Savings Guarantee */}
                        <div className="p-4 rounded-2xl space-y-2 relative overflow-hidden bg-gradient-to-br from-[#8C5826] to-[#6b421a] text-white shadow-xs">
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-white/15">
                                <Shield size={16} className="text-[#F8F246]" />
                            </div>
                            <h4 className="text-xs font-bold">
                                100% Tested & Guaranteed
                            </h4>
                            <p className="text-[11px] leading-relaxed opacity-85">
                                All discount codes and product deals are manually verified to guarantee real savings at checkout.
                            </p>
                        </div>

                    </div>
                </div>

                {/* ━━━ 4. Related Deals from Merchant (Compact 4-col Grid) ━━━ */}
                {relatedProducts?.length > 0 && (
                    <div className="pt-8 space-y-4">
                        <div className="flex items-center justify-between pb-2.5 border-b border-[#f0ede8]">
                            <div className="flex items-center gap-2">
                                <Sparkles size={15} style={{ color: C.BROWN }} />
                                <h2 className="text-sm sm:text-base font-black text-gray-900 tracking-tight">
                                    Related Deals from {merchantName}
                                </h2>
                            </div>
                            <Link
                                href={merchantHref}
                                className="text-xs font-bold text-[#8C5826] hover:underline"
                            >
                                View all deals →
                            </Link>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {relatedProducts.slice(0, 4).map((item: any, i: number) => (
                                <EventOfferCard
                                    key={i}
                                    product={item}
                                    merchantHref={merchantHref}
                                    domain={companyDomain}
                                    merchant_name={merchantName}
                                    merchant_logo={merchantLogo}
                                    productDetailUrl={getProductDetailHref(merchantRes, slugType, item.slug, item?.category?.slug)}
                                />
                            ))}
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}

export default ProductDetailInteractive
